import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActionMenu, Text } from '../../../../components';
import { runningJobCopy } from '../../seo-copy';
import type { SiteSeoJob } from '../../seo-types';
import { DirtyNote, JobNote, MOBILE_NAV_CLEARANCE_PX, NoteSlot, PublishBtn, Root } from './MobileActionBar.styles';

export type MobileActionBarProps = {
  dirty: boolean;
  /** The job running now (never one that has ended). */
  publishing: SiteSeoJob | null;
  /** False: the last publish may not have reached the site, so the primary reads "Publish again". */
  confirmed: boolean;
  canRevert: boolean;
  /** The server's issues hold an error: nothing can be published until it is fixed. */
  blocked: boolean;
  onPublish: () => void;
  onUndo: () => void;
};

/**
 * The SEO page's header actions on a phone, pinned to the bottom of the screen: the draft or job note, Publish
 * (enabled only with changes, nothing running and nothing blocking; "Publish again", enabled even without changes,
 * while the last publish may not have reached the site) and Undo last publish in the menu.
 */
export const MobileActionBar = ({
  dirty,
  publishing,
  confirmed,
  canRevert,
  blocked,
  onPublish,
  onUndo,
}: MobileActionBarProps): React.ReactElement => {
  const { t } = useTranslation();
  const again = !confirmed && publishing === null;
  const canPublish = publishing === null && !blocked && (dirty || again);
  const publish = (): void => {
    if (canPublish) onPublish();
  };
  return (
    <Root role="toolbar" aria-label={t('Publish actions')}>
      <NoteSlot>
        {publishing ? (
          <JobNote text={runningJobCopy(t, publishing)} raw tag="span" />
        ) : again ? (
          <JobNote text="Not on your site yet" tag="span" />
        ) : dirty ? (
          <DirtyNote text="Draft · not published" tag="span" />
        ) : null}
      </NoteSlot>
      <PublishBtn
        type="button"
        aria-disabled={!canPublish}
        title={blocked ? t('Fix the fields marked in red first') : undefined}
        onClick={publish}
      >
        <Text text={again ? 'Publish again' : 'Publish'} />
      </PublishBtn>
      <ActionMenu
        viewportInsets={{ bottom: MOBILE_NAV_CLEARANCE_PX }}
        items={[{ label: t('Undo last publish'), disabled: !canRevert || publishing !== null, onSelect: onUndo }]}
      />
    </Root>
  );
};

export default MobileActionBar;
